/**
 * ArtifactLibrary — the durable anchor layer over the widget stream (Wave 13).
 *
 * Two tabs:
 *  - Library: widgets the user chose to keep (saved_artifacts) — rename,
 *    pin, restore to canvas, delete.
 *  - History: the back-catalog — every widget ever generated (ui_schema is
 *    persisted on each assistant message), restorable or saveable from here,
 *    so "a view I liked disappeared" is always recoverable.
 *
 * Mobile-first: full-screen sheet at 390px, side panel >=768px.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, FolderOpen, History, Pin, Trash2, CornerUpLeft, Bookmark, Pencil, Check,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ArtifactLibrary.css';

const WIDGET_TYPE_LABELS = {
  dynamic: 'Custom view',
  procurement_table: 'Procurement table',
  spend_table: 'Spend table',
  comparison_matrix: 'Comparison',
  capability_tree: 'Capabilities',
  product_cards: 'Products',
  'deal-waterfall': 'Deal',
  day_dashboard: 'Dashboard',
};

function typeLabel(type) {
  return WIDGET_TYPE_LABELS[type] || (type || 'view').replace(/[_-]/g, ' ');
}

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ArtifactLibrary({ open, onClose, onRestore, onSaveFromHistory }) {
  const [tab, setTab] = useState('library');
  const [artifacts, setArtifacts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'library') {
        const data = await apiClient.get('/api/artifacts');
        setArtifacts(data.artifacts || []);
      } else {
        const data = await apiClient.get('/api/artifacts/history?limit=60');
        setHistory(data.widgets || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const togglePin = async (artifact) => {
    try {
      await apiClient.put(`/api/artifacts/${artifact.artifact_id}`, { pinned: !artifact.pinned });
      setArtifacts((prev) => prev.map((a) =>
        a.artifact_id === artifact.artifact_id ? { ...a, pinned: !a.pinned } : a
      ).sort((a, b) => (b.pinned - a.pinned)));
    } catch (e) { setError(e.message); }
  };

  const remove = async (artifact) => {
    try {
      await apiClient.del(`/api/artifacts/${artifact.artifact_id}`);
      setArtifacts((prev) => prev.filter((a) => a.artifact_id !== artifact.artifact_id));
    } catch (e) { setError(e.message); }
  };

  const commitRename = async (artifact) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name || name === artifact.name) return;
    try {
      await apiClient.put(`/api/artifacts/${artifact.artifact_id}`, { name });
      setArtifacts((prev) => prev.map((a) =>
        a.artifact_id === artifact.artifact_id ? { ...a, name } : a
      ));
    } catch (e) { setError(e.message); }
  };

  if (!open) return null;

  return (
    <div className="al-overlay" onClick={onClose}>
      <div className="al-panel" onClick={(e) => e.stopPropagation()}>
        <div className="al-header">
          <div className="al-tabs">
            <button
              className={`al-tab ${tab === 'library' ? 'al-tab-active' : ''}`}
              onClick={() => setTab('library')}
            >
              <FolderOpen size={14} /> Library
            </button>
            <button
              className={`al-tab ${tab === 'history' ? 'al-tab-active' : ''}`}
              onClick={() => setTab('history')}
            >
              <History size={14} /> History
            </button>
          </div>
          <button className="al-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="al-body">
          {error && <div className="al-error">{error}</div>}
          {loading && <div className="al-empty">Loading…</div>}

          {!loading && tab === 'library' && (
            artifacts.length === 0 ? (
              <div className="al-empty">
                <Bookmark size={32} strokeWidth={1} />
                <p>Nothing saved yet</p>
                <span>Use the bookmark on any widget header to keep it here.</span>
              </div>
            ) : (
              artifacts.map((a) => (
                <div key={a.artifact_id} className={`al-card ${a.pinned ? 'al-card-pinned' : ''}`}>
                  <div className="al-card-main">
                    {renamingId === a.artifact_id ? (
                      <span className="al-rename">
                        <input
                          value={renameValue}
                          autoFocus
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && commitRename(a)}
                        />
                        <button onClick={() => commitRename(a)} title="Save name"><Check size={13} /></button>
                      </span>
                    ) : (
                      <span className="al-card-name" title={a.name}>{a.name}</span>
                    )}
                    <span className="al-card-meta">
                      <span className="al-type-badge">{typeLabel(a.widget_type)}</span>
                      {a.account_label && <span className="al-account-chip">{a.account_label}</span>}
                      <span className="al-time">{timeAgo(a.updated_at)}</span>
                    </span>
                  </div>
                  <div className="al-card-actions">
                    <button onClick={() => onRestore(a.ui_schema)} title="Restore to canvas">
                      <CornerUpLeft size={13} />
                    </button>
                    <button
                      className={a.pinned ? 'al-pin-active' : ''}
                      onClick={() => togglePin(a)}
                      title={a.pinned ? 'Unpin' : 'Pin'}
                    >
                      <Pin size={13} />
                    </button>
                    <button
                      onClick={() => { setRenamingId(a.artifact_id); setRenameValue(a.name); }}
                      title="Rename"
                    >
                      <Pencil size={13} />
                    </button>
                    <button className="al-delete" onClick={() => remove(a)} title="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )
          )}

          {!loading && tab === 'history' && (
            history.length === 0 ? (
              <div className="al-empty">
                <History size={32} strokeWidth={1} />
                <p>No generated views yet</p>
              </div>
            ) : (
              history.map((h, i) => (
                <div key={`${h.message_id}-${h.widget_index}-${i}`} className="al-card">
                  <div className="al-card-main">
                    <span className="al-card-name" title={h.widget?.title}>
                      {h.widget?.title || typeLabel(h.widget?.type)}
                    </span>
                    <span className="al-card-meta">
                      <span className="al-type-badge">{typeLabel(h.widget?.type)}</span>
                      <span className="al-time">{timeAgo(h.generated_at)}</span>
                    </span>
                  </div>
                  <div className="al-card-actions">
                    <button onClick={() => onRestore(h.widget)} title="Restore to canvas">
                      <CornerUpLeft size={13} />
                    </button>
                    <button
                      onClick={() => onSaveFromHistory(h)}
                      title="Save to library"
                    >
                      <Bookmark size={13} />
                    </button>
                  </div>
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default ArtifactLibrary;
